import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';

import ApiService from '../../services/apiService';
import { GenericPaginationRequest } from '../../interfaces/services';
import { NetworkAreas } from '../../interfaces/services/areas';
import { ConsigneesResponse } from '../../interfaces/services/consignees';
import { ShippersResponse } from '../../interfaces/services/shippers';
import { NetworkKpi } from '../../interfaces/services/kpis';
import { ApiStatus } from '../../utils/enums';
import { getTTCBaseUrl } from '../../utils/environmentUtils';
import { formatHealthFilters } from '../../utils/stateHelpers';

interface NetworkHealthState {
    networkKpisApiStatus: ApiStatus;
    networkKpis: NetworkKpi;
    networkAreasApiStatus: ApiStatus;
    networkAreas: NetworkAreas;
    unloadTimeDeliveryPartiesApiStatus: ApiStatus;
    unloadTimeDeliveryPartiesApiParams: GenericPaginationRequest;
    unloadTimeDeliveryParties: ConsigneesResponse;
    loadTimePickupPartiesApiStatus: ApiStatus;
    loadTimePickupPartiesApiParams: GenericPaginationRequest;
    loadTimePickupParties: ShippersResponse;
    inTransitDelayPickupPartiesApiStatus: ApiStatus;
    inTransitDelayPickupPartiesApiParams: GenericPaginationRequest;
    inTransitDelayPickupParties: ShippersResponse;
    mphPickupPartiesApiStatus: ApiStatus;
    mphPickupPartiesApiParams: GenericPaginationRequest;
    mphPickupParties: ShippersResponse;
}

const initialState: NetworkHealthState = {
    networkKpisApiStatus: ApiStatus.Idle,
    networkKpis: {
        carrier_network_grade: null,
        lane_count: null,
        freight_order_count: null,
        total_revenue_usd_amount: null,
        linehaul_revenue_usd_amount: null,
        fuel_surcharge_usd_amount: null,
        accessorial_revenue_usd_amount: null,
        travel_distance_miles_amount: null,
        billable_distance_miles_amount: null,
        loaded_distance_miles_amount: null,
        empty_distance_miles_amount: null,
        pta_to_last_stop_departure_hours_duration: null,
        dispatch_to_last_stop_departure_hours_duration: null,
        first_stop_to_last_stop_hours_duration: null,
        first_stop_departure_to_final_stop_arrival_hours_duration: null,
        standard_transit_hours_duration: null,
        netwise_dwell_hours_duration: null,
        netwise_in_transit_delay_hours_duration: null,
        netwise_delay_hours_duration: null,
        load_hours_duration: null,
        unload_hours_duration: null,
        empty_transit_hours_duration: null,
        pickup_party_count: null,
        commodity_count: null,
        average_dispatch_to_last_stop_departure_hours_duration: null,
        average_first_stop_to_last_stop_hours_duration: null,
        average_first_stop_departure_to_final_stop_arrival_hours_duration: null,
        average_standard_transit_hours_duration: null,
        average_netwise_in_transit_delay_hours_duration: null,
        average_load_hours_duration: null,
        average_unload_hours_duration: null,
        average_empty_transit_hours_duration: null,
        average_network_order_yield_usd: null,
    },
    networkAreasApiStatus: ApiStatus.Idle,
    networkAreas: {
        areas_count: 0,
        areas_health: {
            area_daily_balance_grade: null,
            area_daily_density_grade: null,
            area_empty_miles_percentage_grade: null,
        },
        areas: [],
        page_number: 0,
        size: 0,
        pages: 0,
    },
    unloadTimeDeliveryPartiesApiStatus: ApiStatus.Idle,
    unloadTimeDeliveryPartiesApiParams: {
        pageNumber: 1,
        pageSize: 10,
    },
    unloadTimeDeliveryParties: {
        delivery_party_count: 0,
        delivery_party_health: {
            unload_hours_duration_grade: null,
            netwise_in_transit_delay_hours_ratio_grade: null,
            scheduled_vs_actual_miles_per_hour_grade: null,
        },
        delivery_parties: [],
        page_number: 0,
        size: 0,
        pages: 0,
    },
    loadTimePickupPartiesApiStatus: ApiStatus.Idle,
    loadTimePickupPartiesApiParams: {
        pageNumber: 1,
        pageSize: 10,
    },
    loadTimePickupParties: {
        pickup_party_count: 0,
        pickup_party_health: {
            load_hours_duration_grade: null,
            netwise_in_transit_delay_hours_ratio_grade: null,
            scheduled_vs_actual_miles_per_hour_grade: null,
        },
        pickup_parties: [],
        page_number: 0,
        size: 0,
        pages: 0,
    },
    inTransitDelayPickupPartiesApiStatus: ApiStatus.Idle,
    inTransitDelayPickupPartiesApiParams: {
        pageNumber: 1,
        pageSize: 10,
    },
    inTransitDelayPickupParties: {
        pickup_party_count: 0,
        pickup_party_health: {
            load_hours_duration_grade: null,
            netwise_in_transit_delay_hours_ratio_grade: null,
            scheduled_vs_actual_miles_per_hour_grade: null,
        },
        pickup_parties: [],
        page_number: 0,
        size: 0,
        pages: 0,
    },
    mphPickupPartiesApiStatus: ApiStatus.Idle,
    mphPickupPartiesApiParams: {
        pageNumber: 1,
        pageSize: 10,
    },
    mphPickupParties: {
        pickup_party_count: 0,
        pickup_party_health: {
            load_hours_duration_grade: null,
            netwise_in_transit_delay_hours_ratio_grade: null,
            scheduled_vs_actual_miles_per_hour_grade: null,
        },
        pickup_parties: [],
        page_number: 0,
        size: 0,
        pages: 0,
    },
};

export const fetchNetworkKpis = createAsyncThunk(
    'networkHealth/fetchNetworkKpis',
    async () => {
        try {
            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/kpi`,
                body: {
                    filter_criteria: formatHealthFilters(),
                },
            }) as AxiosResponse<NetworkKpi>;

            return data;
        } catch (error) {
            console.error('Unable to fetch network KPIs', error);
            toast.error('Unable to fetch network KPIs');
            throw error;
        }
    }
);

export const fetchNetworkAreas = createAsyncThunk(
    'networkHealth/fetchNetworkAreas',
    async () => {
        try {
            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/areas`,
                body: {
                    filter_criteria: formatHealthFilters(),
                },
            }) as AxiosResponse<NetworkAreas>;

            return data;
        } catch (error) {
            console.error('Unable to fetch network areas', error);
            toast.error('Unable to fetch network areas');
            throw error;
        }
    }
);

// TODO: TCSC-50464
export const fetchUnloadTimeDeliveryParties = createAsyncThunk(
    'networkHealth/fetchUnloadTimeDeliveryParties',
    async () => {
        try {
            // const urlParams = new URLSearchParams();
            // urlParams.append('sort_by', '-average_unload_hours_duration,%2Bdelivery_party_name');
            // urlParams.append('page_number', pageNumber.toString());
            // urlParams.append('size', pageSize.toString());

            const { data } = await ApiService.post({
                // url: `${getTTCBaseUrl()}/planning/v2/insights/network/delivery-parties?${urlParams}`,
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/delivery-parties`,
                body: {
                    filter_criteria: formatHealthFilters(),
                },
            }) as AxiosResponse<ConsigneesResponse>;

            return data;
        } catch (error) {
            console.error('Unable to fetch unload time consignees', error);
            toast.error('Unable to fetch unload time consignees');
            throw error;
        }
    }
);

// TODO: TCSC-50464
export const fetchLoadTimePickupParties = createAsyncThunk(
    'networkHealth/fetchLoadTimePickupParties',
    async () => {
        try {
            // const urlParams = new URLSearchParams();
            // urlParams.append('sort_by', '-average_load_hours_duration,%2Bpickup_party_name');
            // urlParams.append('page_number', pageNumber.toString());
            // urlParams.append('size', pageSize.toString());

            const { data } = await ApiService.post({
                // url: `${getTTCBaseUrl()}/planning/v2/insights/network/pickup-parties?${urlParams}`,
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/pickup-parties`,
                body: {
                    filter_criteria: formatHealthFilters(),
                },
            }) as AxiosResponse<ShippersResponse>;

            return data;
        } catch (error) {
            console.error('Unable to fetch load time shippers', error);
            toast.error('Unable to fetch load time shippers');
            throw error;
        }
    }
);

// TODO: TCSC-50774
export const fetchInTransitDelayPickupParties = createAsyncThunk(
    'networkHealth/fetchInTransitDelayPickupParties',
    async () => {
        try {
            // const urlParams = new URLSearchParams();
            // urlParams.append('sort_by', '-average_netwise_in_transit_delay_hours_duration,%2Bpickup_party_name');
            // urlParams.append('page_number', pageNumber.toString());
            // urlParams.append('size', pageSize.toString());

            const { data } = await ApiService.post({
                // url: `${getTTCBaseUrl()}/planning/v2/insights/network/pickup-parties?${urlParams}`,
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/pickup-parties`,
                body: {
                    filter_criteria: formatHealthFilters(),
                },
            }) as AxiosResponse<ShippersResponse>;

            return data;
        } catch (error) {
            console.error('Unable to fetch in transit delay shippers', error);
            toast.error('Unable to fetch in transit delay shippers');
            throw error;
        }
    }
);

export const fetchMPHPickupParties = createAsyncThunk(
    'networkHealth/fetchMPHPickupParties',
    async ({
        pageNumber,
        pageSize,
    }: GenericPaginationRequest) => {
        try {
            const urlParams = new URLSearchParams();
            urlParams.append('sort_by', '-average_actual_miles_per_hour,-average_scheduled_miles_per_hour,%2Bpickup_party_name');
            urlParams.append('page_number', pageNumber.toString());
            urlParams.append('size', pageSize.toString());

            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/pickup-parties?${urlParams}`,
                body: {
                    filter_criteria: formatHealthFilters(),
                },
            }) as AxiosResponse<ShippersResponse>;

            return data;
        } catch (error) {
            console.error('Unable to fetch MPH shippers', error);
            toast.error('Unable to fetch MPH shippers');
            throw error;
        }
    }
);

export const networkHealthSlice = createSlice({
    name: 'networkHealth',
    initialState,
    reducers: {
        updateUnloadTimeDeliveryPartiesApiParams: (state, action: PayloadAction<GenericPaginationRequest>) => {
            state.unloadTimeDeliveryPartiesApiParams = action.payload;
        },
        updateLoadTimePickupPartiesApiParams: (state, action: PayloadAction<GenericPaginationRequest>) => {
            state.loadTimePickupPartiesApiParams = action.payload;
        },
        updateInTransitDelayPickupPartiesApiParams: (state, action: PayloadAction<GenericPaginationRequest>) => {
            state.inTransitDelayPickupPartiesApiParams = action.payload;
        },
        updateMPHPickupPartiesApiParams: (state, action: PayloadAction<GenericPaginationRequest>) => {
            state.mphPickupPartiesApiParams = action.payload;
        },
    },
    extraReducers(builder) {
        builder.addCase(fetchNetworkKpis.pending, (state) => {
            state.networkKpisApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchNetworkKpis.fulfilled, (state, action) => {
            state.networkKpisApiStatus = ApiStatus.Success;
            state.networkKpis = action.payload;
        });
        builder.addCase(fetchNetworkKpis.rejected, (state) => {
            state.networkKpisApiStatus = ApiStatus.Failure;
            state.networkKpis = initialState.networkKpis;
        });

        builder.addCase(fetchNetworkAreas.pending, (state) => {
            state.networkAreasApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchNetworkAreas.fulfilled, (state, action) => {
            state.networkAreasApiStatus = ApiStatus.Success;
            state.networkAreas = action.payload;
        });
        builder.addCase(fetchNetworkAreas.rejected, (state) => {
            state.networkAreasApiStatus = ApiStatus.Failure;
            state.networkAreas = initialState.networkAreas;
        });

        builder.addCase(fetchUnloadTimeDeliveryParties.pending, (state) => {
            state.unloadTimeDeliveryPartiesApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchUnloadTimeDeliveryParties.fulfilled, (state, action) => {
            state.unloadTimeDeliveryPartiesApiStatus = ApiStatus.Success;
            state.unloadTimeDeliveryParties = action.payload;
        });
        builder.addCase(fetchUnloadTimeDeliveryParties.rejected, (state) => {
            state.unloadTimeDeliveryPartiesApiStatus = ApiStatus.Failure;
            state.unloadTimeDeliveryParties = initialState.unloadTimeDeliveryParties;
        });

        builder.addCase(fetchLoadTimePickupParties.pending, (state) => {
            state.loadTimePickupPartiesApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchLoadTimePickupParties.fulfilled, (state, action) => {
            state.loadTimePickupPartiesApiStatus = ApiStatus.Success;
            state.loadTimePickupParties = action.payload;
        });
        builder.addCase(fetchLoadTimePickupParties.rejected, (state) => {
            state.loadTimePickupPartiesApiStatus = ApiStatus.Failure;
            state.loadTimePickupParties = initialState.loadTimePickupParties;
        });

        builder.addCase(fetchInTransitDelayPickupParties.pending, (state) => {
            state.inTransitDelayPickupPartiesApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchInTransitDelayPickupParties.fulfilled, (state, action) => {
            state.inTransitDelayPickupPartiesApiStatus = ApiStatus.Success;
            state.inTransitDelayPickupParties = action.payload;
        });
        builder.addCase(fetchInTransitDelayPickupParties.rejected, (state) => {
            state.inTransitDelayPickupPartiesApiStatus = ApiStatus.Failure;
            state.inTransitDelayPickupParties = initialState.inTransitDelayPickupParties;
        });

        builder.addCase(fetchMPHPickupParties.pending, (state) => {
            state.mphPickupPartiesApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchMPHPickupParties.fulfilled, (state, action) => {
            state.mphPickupPartiesApiStatus = ApiStatus.Success;
            state.mphPickupParties = action.payload;
        });
        builder.addCase(fetchMPHPickupParties.rejected, (state) => {
            state.mphPickupPartiesApiStatus = ApiStatus.Failure;
            state.mphPickupParties = initialState.mphPickupParties;
        });
    },
});

export const {
    updateUnloadTimeDeliveryPartiesApiParams,
    updateLoadTimePickupPartiesApiParams,
    updateInTransitDelayPickupPartiesApiParams,
    updateMPHPickupPartiesApiParams,
} = networkHealthSlice.actions;
export default networkHealthSlice.reducer;
